import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from '../../context/LanguageContext';
import {
    Search, Plus, Filter, ArrowUpDown, Briefcase, Clock, MapPin,
    User, Calendar, X, ChevronDown, Mail, Phone, Building2, Loader2,
    Trash2, Power, PowerOff
} from 'lucide-react';
import {
    getPublicRequests,
    getMyRequests,
    deleteRequest,
    updateRequest,
    REQUEST_TYPES,
    URGENCY_BADGES,
    getTypeColor,
    getTypeLabel,
    getUrgencyColor,
    getUrgencyLabel,
    getCompensationColor,
    getCompensationLabel,
    getDurationColor,
    getDurationLabel,
    getCreatorProfile
} from '../../services/workRequestsService';
import { getStoredUser as getUser } from '../../context/AuthContext';
import CreateWorkRequest from './CreateWorkRequest';
import WorkRequestDetail from './WorkRequestDetail';

/**
 * Profile Hover Card - Mini modal qui s'affiche au hover sur le créateur
 */
const ProfileHoverCard = ({ peopleUuid, position, onClose }) => {
    const [profile, setProfile] = useState(null);
    const [photoUrl, setPhotoUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const cardRef = useRef(null);

    useEffect(() => {
        const loadProfile = async () => {
            setLoading(true);
            const result = await getCreatorProfile(peopleUuid);
            if (result.success) {
                setProfile(result.profile);
                setPhotoUrl(result.photoUrl);
            }
            setLoading(false);
        };
        if (peopleUuid) {
            loadProfile();
        }
    }, [peopleUuid]);

    // Calculate position to avoid going off screen
    const cardStyle = {
        position: 'fixed',
        left: Math.min(position.x + 10, window.innerWidth - 320),
        top: Math.min(position.y + 10, window.innerHeight - 250),
        zIndex: 100
    };

    return (
        <div
            ref={cardRef}
            style={cardStyle}
            className="bg-white rounded-xl shadow-xl border border-gray-200 p-4 w-72 animate-in fade-in zoom-in-95 duration-150"
            onMouseLeave={onClose}
        >
            {loading ? (
                <div className="flex items-center justify-center py-6">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                </div>
            ) : profile ? (
                <div>
                    {/* Header with photo and name */}
                    <div className="flex items-center gap-3 mb-3">
                        {photoUrl ? (
                            <img
                                src={photoUrl}
                                alt=""
                                className="w-14 h-14 rounded-full object-cover border-2 border-white shadow"
                            />
                        ) : (
                            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-semibold text-lg">
                                {(profile.firstname?.[0] || '').toUpperCase()}
                                {(profile.lastname?.[0] || '').toUpperCase()}
                            </div>
                        )}
                        <div>
                            <h3 className="font-semibold text-gray-900">
                                {profile.firstname} {profile.lastname}
                            </h3>
                            {profile.company && (
                                <p className="text-sm text-gray-500">{profile.company}</p>
                            )}
                        </div>
                    </div>

                    {/* Contact info */}
                    <div className="space-y-2 text-sm">
                        {profile.company && (
                            <div className="flex items-center gap-2 text-gray-600">
                                <Building2 size={14} className="text-gray-400" />
                                <span>{profile.company}</span>
                            </div>
                        )}
                        {profile.email && (
                            <div className="flex items-center gap-2 text-gray-600">
                                <Mail size={14} className="text-gray-400" />
                                <a
                                    href={`mailto:${profile.email}`}
                                    className="hover:text-blue-500 truncate"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {profile.email}
                                </a>
                            </div>
                        )}
                        {(profile.phone || profile.gsm) && (
                            <div className="flex items-center gap-2 text-gray-600">
                                <Phone size={14} className="text-gray-400" />
                                <a
                                    href={`tel:${profile.phone || profile.gsm}`}
                                    className="hover:text-blue-500"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {profile.phone || profile.gsm}
                                </a>
                            </div>
                        )}
                        {profile.googleaddress && (
                            <div className="flex items-center gap-2 text-gray-600 pt-2 border-t">
                                <MapPin size={14} className="text-gray-400 flex-shrink-0" />
                                <span className="text-xs">{profile.googleaddress}</span>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div className="text-center py-4 text-gray-500">
                    Profil non disponible
                </div>
            )}
        </div>
    );
};

const WorkRequests = () => {
    const { t } = useTranslation();
    const user = getUser();

    // State
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterUrgency, setFilterUrgency] = useState('');
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortDir, setSortDir] = useState('DESC');
    const [showFilters, setShowFilters] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [actionLoading, setActionLoading] = useState(null);
    const [viewMode, setViewMode] = useState('all'); // 'all' or 'mine'

    // Hover card state
    const [hoverCard, setHoverCard] = useState(null);
    const hoverTimeout = useRef(null);

    // Load requests
    const loadRequests = async () => {
        setLoading(true);
        let data;
        const userId = user?.id || user?.ID;
        console.log('user:', user, 'userId:', userId, 'viewMode:', viewMode);
        if (viewMode === 'mine' && userId) {
            // Load user's own requests (including closed ones)
            data = await getMyRequests(userId);
        } else {
            // Load public requests
            data = await getPublicRequests({
                type: filterType,
                urgency: filterUrgency,
                search: search,
                sortBy: sortBy,
                sortDir: sortDir
            });
        }
        setRequests(data);
        setLoading(false);
    };

    useEffect(() => {
        loadRequests();
    }, [filterType, filterUrgency, sortBy, sortDir, viewMode]);

    // Search with debounce
    useEffect(() => {
        const timer = setTimeout(() => {
            loadRequests();
        }, 300);
        return () => clearTimeout(timer);
    }, [search]);

    // Format date
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    // Toggle sort
    const toggleSort = (field) => {
        if (sortBy === field) {
            setSortDir(sortDir === 'DESC' ? 'ASC' : 'DESC');
        } else {
            setSortBy(field);
            setSortDir('DESC');
        }
    };

    // Clear filters
    const clearFilters = () => {
        setSearch('');
        setFilterType('');
        setFilterUrgency('');
        setSortBy('createdAt');
        setSortDir('DESC');
    };

    // Handle creator hover
    const handleCreatorMouseEnter = (e, request) => {
        if (!request.creatorpeopleuuid) return;

        // Clear any existing timeout
        if (hoverTimeout.current) {
            clearTimeout(hoverTimeout.current);
        }

        // Delay before showing card to avoid flicker
        hoverTimeout.current = setTimeout(() => {
            setHoverCard({
                peopleUuid: request.creatorpeopleuuid,
                position: { x: e.clientX, y: e.clientY }
            });
        }, 300);
    };

    const handleCreatorMouseLeave = () => {
        if (hoverTimeout.current) {
            clearTimeout(hoverTimeout.current);
        }
        // Small delay before hiding to allow mouse to move to card
        hoverTimeout.current = setTimeout(() => {
            setHoverCard(null);
        }, 100);
    };

    const hasActiveFilters = search || filterType || filterUrgency;

    // Check if user owns the request
    const isOwner = (request) => {
        return user?.id && request.peopleid === user.id;
    };

    // Handle delete request
    const handleDelete = async (request) => {
        setActionLoading(request.id);
        const result = await deleteRequest(request.id, user.id);
        setActionLoading(null);
        setConfirmDelete(null);
        if (result.success) {
            loadRequests();
        }
    };

    // Handle toggle active/inactive/closed
    const handleToggleStatus = async (request) => {
        setActionLoading(request.id);
        // If closed or inactive, reactivate. If active, deactivate.
        const newStatus = request.status === 'active' ? 'inactive' : 'active';
        const result = await updateRequest(request.id, {
            userId: user.id,
            status: newStatus
        });
        setActionLoading(null);
        if (result.success) {
            loadRequests();
        }
    };

    return (
        <div className="max-w-6xl mx-auto p-4">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                        <Briefcase className="w-7 h-7 text-blue-500" />
                        {t.work?.title || 'Demandes de travail'}
                    </h1>
                    <p className="text-gray-500 mt-1">
                        {t.work?.subtitle || 'Trouvez ou proposez des opportunités'}
                    </p>
                </div>
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                    <Plus className="w-5 h-5" />
                    {t.work?.create || 'Nouvelle demande'}
                </button>
            </div>

            {/* Search and Filters */}
            <div className="bg-white rounded-xl shadow-sm border p-4 mb-6">
                {/* View Mode Tabs */}
                {user && (
                    <div className="flex gap-2 mb-4 pb-4 border-b">
                        <button
                            onClick={() => setViewMode('all')}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                viewMode === 'all'
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            Toutes les demandes
                        </button>
                        <button
                            onClick={() => setViewMode('mine')}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                viewMode === 'mine'
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            Mes demandes
                        </button>
                    </div>
                )}

                <div className="flex flex-col sm:flex-row gap-4">
                    {/* Search */}
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder={t.work?.searchPlaceholder || 'Rechercher par compétence, métier...'}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    {/* Filter Toggle */}
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                            showFilters || hasActiveFilters ? 'bg-blue-50 border-blue-300 text-blue-700' : 'hover:bg-gray-50'
                        }`}
                    >
                        <Filter className="w-5 h-5" />
                        {t.work?.filters || 'Filtres'}
                        {hasActiveFilters && (
                            <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
                                {[search, filterType, filterUrgency].filter(Boolean).length}
                            </span>
                        )}
                        <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                    </button>
                </div>

                {/* Expanded Filters */}
                {showFilters && (
                    <div className="mt-4 pt-4 border-t grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {/* Type Filter */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t.work?.filterType || 'Type de demande'}
                            </label>
                            <select
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">{t.work?.allTypes || 'Tous les types'}</option>
                                {Object.entries(REQUEST_TYPES).map(([key, { label }]) => (
                                    <option key={key} value={key}>{label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Urgency Filter */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t.work?.filterUrgency || 'Urgence'}
                            </label>
                            <select
                                value={filterUrgency}
                                onChange={(e) => setFilterUrgency(e.target.value)}
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">{t.work?.allUrgencies || 'Toutes les urgences'}</option>
                                {Object.entries(URGENCY_BADGES).map(([key, { label }]) => (
                                    <option key={key} value={key}>{label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Sort */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t.work?.sortBy || 'Trier par'}
                            </label>
                            <div className="flex gap-2">
                                <select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="createdAt">{t.work?.sortDate || 'Date'}</option>
                                    <option value="urgency">{t.work?.sortUrgency || 'Urgence'}</option>
                                    <option value="type">{t.work?.sortType || 'Type'}</option>
                                </select>
                                <button
                                    onClick={() => setSortDir(sortDir === 'DESC' ? 'ASC' : 'DESC')}
                                    className="px-3 py-2 border rounded-lg hover:bg-gray-50"
                                    title={sortDir === 'DESC' ? 'Décroissant' : 'Croissant'}
                                >
                                    <ArrowUpDown className={`w-5 h-5 ${sortDir === 'ASC' ? 'rotate-180' : ''}`} />
                                </button>
                            </div>
                        </div>

                        {/* Clear Filters */}
                        {hasActiveFilters && (
                            <div className="sm:col-span-3">
                                <button
                                    onClick={clearFilters}
                                    className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
                                >
                                    <X className="w-4 h-4" />
                                    {t.work?.clearFilters || 'Effacer les filtres'}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Results Count */}
            <div className="text-sm text-gray-500 mb-4">
                {loading ? (
                    t.work?.loading || 'Chargement...'
                ) : (
                    `${requests.length} ${t.work?.resultsCount || 'demande(s) trouvée(s)'}`
                )}
            </div>

            {/* Requests List */}
            {loading ? (
                <div className="grid gap-4">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="bg-white rounded-xl shadow-sm border p-6 animate-pulse">
                            <div className="h-6 bg-gray-200 rounded w-2/3 mb-4"></div>
                            <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                        </div>
                    ))}
                </div>
            ) : requests.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
                    <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-600 mb-2">
                        {t.work?.noResults || 'Aucune demande trouvée'}
                    </h3>
                    <p className="text-gray-400">
                        {hasActiveFilters
                            ? (t.work?.tryOtherFilters || "Essayez d'autres filtres")
                            : (t.work?.beFirst || 'Soyez le premier à publier une demande !')
                        }
                    </p>
                </div>
            ) : (
                <div className="grid gap-4">
                    {requests.map(request => (
                        <div
                            key={request.id}
                            onClick={() => setSelectedRequest(request)}
                            className={`bg-white rounded-xl shadow-sm border p-6 hover:shadow-md transition-shadow cursor-pointer ${
                                request.status === 'closed' ? 'opacity-60 border-gray-300' : ''
                            }`}
                        >
                            <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                                {/* Creator Avatar - with hover */}
                                <div
                                    className="flex-shrink-0 relative"
                                    onMouseEnter={(e) => handleCreatorMouseEnter(e, request)}
                                    onMouseLeave={handleCreatorMouseLeave}
                                >
                                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-semibold cursor-pointer hover:ring-2 hover:ring-blue-300 hover:ring-offset-2 transition-all">
                                        {(request.creatorfirstname?.[0] || '').toUpperCase()}
                                        {(request.creatorlastname?.[0] || '').toUpperCase()}
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                    {/* Title & Badges */}
                                    <div className="flex flex-wrap items-center gap-2 mb-2">
                                        <h3 className="text-lg font-semibold text-gray-800 truncate">
                                            {request.title}
                                        </h3>
                                        {/* Closed Badge */}
                                        {request.status === 'closed' && (
                                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-500 text-white">
                                                Clôturée
                                            </span>
                                        )}
                                        {/* Type Badge */}
                                        <span
                                            className="px-2 py-0.5 text-xs font-medium rounded-full text-white"
                                            style={{ backgroundColor: getTypeColor(request.requesttype) }}
                                        >
                                            {getTypeLabel(request.requesttype)}
                                        </span>
                                        {/* Urgency Badge */}
                                        <span
                                            className="px-2 py-0.5 text-xs font-medium rounded-full text-white"
                                            style={{ backgroundColor: getUrgencyColor(request.urgencybadge) }}
                                        >
                                            {getUrgencyLabel(request.urgencybadge)}
                                        </span>
                                        {/* Compensation Badge */}
                                        {request.compensation && (
                                            <span
                                                className="px-2 py-0.5 text-xs font-medium rounded-full text-white"
                                                style={{ backgroundColor: getCompensationColor(request.compensation) }}
                                            >
                                                {getCompensationLabel(request.compensation)}
                                            </span>
                                        )}
                                        {/* Duration Badge */}
                                        {request.duration && (
                                            <span
                                                className="px-2 py-0.5 text-xs font-medium rounded-full text-white"
                                                style={{ backgroundColor: getDurationColor(request.duration) }}
                                            >
                                                {getDurationLabel(request.duration)}
                                            </span>
                                        )}
                                    </div>

                                    {/* Description */}
                                    <p className="text-gray-600 line-clamp-2 mb-3">
                                        {request.description}
                                    </p>

                                    {/* Meta */}
                                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                                        <span
                                            className="flex items-center gap-1 hover:text-blue-500 cursor-pointer"
                                            onMouseEnter={(e) => handleCreatorMouseEnter(e, request)}
                                            onMouseLeave={handleCreatorMouseLeave}
                                        >
                                            <User className="w-4 h-4" />
                                            {request.creatorfirstname} {request.creatorlastname}
                                            {request.creatorcompany && ` • ${request.creatorcompany}`}
                                        </span>
                                        {request.location && (
                                            <span className="flex items-center gap-1">
                                                <MapPin className="w-4 h-4" />
                                                {request.location}
                                            </span>
                                        )}
                                        <span className="flex items-center gap-1">
                                            <Calendar className="w-4 h-4" />
                                            {formatDate(request.createdat)}
                                        </span>
                                    </div>

                                    {/* Skills */}
                                    {request.skillsneeded && (
                                        <div className="mt-3 flex flex-wrap gap-2">
                                            {request.skillsneeded.split(',').map((skill, idx) => (
                                                <span
                                                    key={idx}
                                                    className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded"
                                                >
                                                    {skill.trim()}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Owner Actions */}
                                {isOwner(request) && (
                                    <div className="flex flex-col gap-2 ml-4">
                                        {/* Toggle Active/Inactive/Closed */}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleToggleStatus(request);
                                            }}
                                            disabled={actionLoading === request.id}
                                            className={`p-2 rounded-lg transition-colors ${
                                                request.status === 'active'
                                                    ? 'text-amber-600 hover:bg-amber-50'
                                                    : 'text-green-600 hover:bg-green-50'
                                            }`}
                                            title={request.status === 'active' ? 'Désactiver' : 'Réactiver'}
                                        >
                                            {actionLoading === request.id ? (
                                                <Loader2 className="w-5 h-5 animate-spin" />
                                            ) : request.status === 'active' ? (
                                                <PowerOff className="w-5 h-5" />
                                            ) : (
                                                <Power className="w-5 h-5" />
                                            )}
                                        </button>

                                        {/* Delete */}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setConfirmDelete(request);
                                            }}
                                            disabled={actionLoading === request.id}
                                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                            title="Supprimer"
                                        >
                                            <Trash2 className="w-5 h-5" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Profile Hover Card */}
            {hoverCard && (
                <ProfileHoverCard
                    peopleUuid={hoverCard.peopleUuid}
                    position={hoverCard.position}
                    onClose={() => setHoverCard(null)}
                />
            )}

            {/* Create Modal */}
            {showCreateModal && (
                <CreateWorkRequest
                    onClose={() => setShowCreateModal(false)}
                    onCreated={() => {
                        setShowCreateModal(false);
                        loadRequests();
                    }}
                />
            )}

            {/* Detail Modal */}
            {selectedRequest && (
                <WorkRequestDetail
                    request={selectedRequest}
                    onClose={() => setSelectedRequest(null)}
                    onUpdated={() => {
                        setSelectedRequest(null);
                        loadRequests();
                    }}
                />
            )}

            {/* Delete Confirmation Modal */}
            {confirmDelete && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">
                            Confirmer la suppression
                        </h3>
                        <p className="text-gray-600 mb-6">
                            Êtes-vous sûr de vouloir supprimer la demande <strong>"{confirmDelete.title}"</strong> ? Cette action est irréversible.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setConfirmDelete(null)}
                                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={() => handleDelete(confirmDelete)}
                                disabled={actionLoading}
                                className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
                            >
                                {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                                Supprimer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WorkRequests;
